import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import MovementDetailDialog from "../../../components/dashboard/erp/MovementDetailDialog";
import MovementFormDialog from "../../../components/dashboard/erp/MovementFormDialog";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle, MovementStatusChip } from "../../../components/dashboard/erp/ErpUi";
import ResponsiveListControls from "../../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import {
  ERP_BRAND,
  MOVEMENT_KIND_LABELS,
  entityId,
  formatDateTime,
  formatMoney,
} from "../../../utils/erp";
import { hasAnyProjectPermission, hasProjectPermission } from "../../../utils/projectAccess";
import { isProjectCapabilityEnabled } from "../../../utils/projectCapabilities";
import { responsiveActionsSx } from "../../../theme/webProfitResponsive";

const KIND_OPTIONS = ["sale", "purchase", "expense", "opening_balance", "stock_adjustment", "collection", "payment", "transfer", "cash_in", "cash_out", "reversal"];

export default function ProjectErpMovementsPage() {
  const { projectId } = useParams();
  const { project, bootstrap, refreshBootstrap, access } = useOutletContext();
  const canCreate = hasAnyProjectPermission(access, [
    "erp.sales.create",
    "erp.purchases.create",
    "erp.expenses.create",
    "erp.stock.manage",
    "erp.collections.create",
    "erp.payments.create",
    "cash.operations.create",
    "cash.operations.in",
    "cash.operations.out",
  ]);
  const canCreateSale = hasProjectPermission(access, "erp.sales.create");
  const canReadCustomers = hasProjectPermission(access, "customers.read");
  const membershipsEnabled = isProjectCapabilityEnabled(project, "memberships");
  const needsMoneySetup = !(bootstrap.registers || []).length;
  const canUpdate = hasProjectPermission(access, "erp.movements.update");
  const canConfirm = hasProjectPermission(access, "erp.movements.confirm");
  const canVoid = hasProjectPermission(access, "erp.movements.void");
  const [searchParams, setSearchParams] = useSearchParams();
  const salesView = searchParams.get("vista") === "ventas";
  const canCreateInView = salesView ? canCreateSale : canCreate;
  const [movements, setMovements] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [filters, setFilters] = React.useState({ q: "", kind: salesView ? "sale" : "", status: "", dateFrom: "", dateTo: "" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [formOpen, setFormOpen] = React.useState(searchParams.get("nuevo") === "1");
  const initialKind = searchParams.get("tipo") || (salesView ? "sale" : "");
  const initialProductId = searchParams.get("itemId") || "";
  const [editing, setEditing] = React.useState(null);
  const [detailId, setDetailId] = React.useState(searchParams.get("movementId") || "");
  const [action, setAction] = React.useState(null);
  const [actionReason, setActionReason] = React.useState("");
  const [processing, setProcessing] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listMovements(projectId, {
        page: page + 1,
        pageSize: rowsPerPage,
        ...filters,
      });
      setMovements(data.movements || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message || "No se pudieron cargar los movimientos");
      setMovements([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  React.useEffect(() => {
    const timeout = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [load, filters.q]);

  React.useEffect(() => {
    if (searchParams.get("nuevo") === "1") setFormOpen(true);
    const requestedMovementId = searchParams.get("movementId") || "";
    if (requestedMovementId) setDetailId(requestedMovementId);
  }, [searchParams]);

  React.useEffect(() => {
    setPage(0);
    setFilters((current) => {
      if (salesView) {
        return current.kind === "sale" ? current : { ...current, kind: "sale" };
      }
      return current.kind === "sale" ? { ...current, kind: "" } : current;
    });
  }, [salesView]);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    if (searchParams.has("nuevo") || searchParams.has("tipo") || searchParams.has("itemId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("nuevo");
      next.delete("tipo");
      next.delete("itemId");
      setSearchParams(next, { replace: true });
    }
  };

  const closeDetail = () => {
    setDetailId("");
    if (!searchParams.has("movementId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("movementId");
    setSearchParams(next, { replace: true });
  };

  const performAction = async () => {
    if (!action?.movement) return;
    try {
      setProcessing(true);
      setError("");
      const id = entityId(action.movement);
      if (action.type === "confirm") {
        await erpApi.confirmMovement(projectId, id);
      } else {
        if (!actionReason.trim()) {
          setError("Indicá el motivo de la anulación.");
          return;
        }
        await erpApi.voidMovement(projectId, id, actionReason);
      }
      setAction(null);
      setActionReason("");
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudo procesar el movimiento");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Stack spacing={2.3}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

      <ErpSectionTitle
        title={salesView ? "Ventas registradas" : "Lo que pasó en tu negocio"}
        description={salesView
          ? "Ventas registradas en Gestión. Los pedidos web siguen separados en Pedidos."
          : "Ventas, compras, gastos, cobros, transferencias y stock en un solo historial."}
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={responsiveActionsSx}>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>
              Actualizar
            </Button>
            {canCreateInView ? (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setFormOpen(true)} sx={{ textTransform: "none", fontWeight: 850 }}>
                {salesView ? "Registrar venta" : "Registrar algo"}
              </Button>
            ) : null}
          </Stack>
        }
      />

      {needsMoneySetup ? (
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" href={`/dashboard/proyectos/${projectId}/sistema/caja?crear=1`}>
              Crear cajas
            </Button>
          )}
        >
          Primer paso: creá los lugares reales donde manejás dinero, por ejemplo Efectivo, Banco Nación o Mercado Pago.
        </Alert>
      ) : null}

      <ErpPanel>
        <Box sx={{ p: { xs: 1.1, sm: 2.2 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <ResponsiveListControls
            search={(
              <TextField
                size="small"
                fullWidth
                placeholder="Buscar movimiento o referencia"
                value={filters.q}
                onChange={(event) => setFilter("q", event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
              />
            )}
            activeFilterCount={[
              !salesView && filters.kind,
              filters.status,
              filters.dateFrom,
              filters.dateTo,
            ].filter(Boolean).length}
            onClear={() => {
              setFilters((current) => ({
                ...current,
                kind: salesView ? "sale" : "",
                status: "",
                dateFrom: "",
                dateTo: "",
              }));
              setPage(0);
            }}
            drawerTitle="Filtrar movimientos"
          >
            <TextField select size="small" label="Tipo" value={filters.kind} onChange={(event) => setFilter("kind", event.target.value)} disabled={salesView} sx={{ minWidth: { sm: 190 } }}>
              <MenuItem value="">Todos</MenuItem>
              {KIND_OPTIONS.map((kind) => <MenuItem key={kind} value={kind}>{MOVEMENT_KIND_LABELS[kind]}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Estado" value={filters.status} onChange={(event) => setFilter("status", event.target.value)} sx={{ minWidth: { sm: 150 } }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="draft">Borrador</MenuItem>
              <MenuItem value="confirmed">Confirmado</MenuItem>
              <MenuItem value="voided">Anulado</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
          </ResponsiveListControls>
        </Box>

        {loading ? <ErpLoading label="Cargando movimientos..." /> : !movements.length ? (
          <ErpEmpty
            title={salesView ? "Todavía no hay ventas registradas" : "No hay movimientos para estos filtros"}
            description={salesView
              ? "Cuando registres una venta desde Gestión, aparecerá acá sin mezclarla con los pedidos recibidos desde la web."
              : "Cuando registres algo que pasó, acá vas a ver toda la historia del negocio sin necesidad de pensar en asientos contables."}
            action={canCreateInView ? <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setFormOpen(true)}>{salesView ? "Registrar venta" : "Registrar algo"}</Button> : null}
          />
        ) : (
          <>
            <TableContainer>
              <ResponsiveOperationalTable
                size="small"
                mobileLabel={salesView ? "Ventas registradas" : "Movimientos"}
                collapseAfter={2}
                mobileEssentialLabels={["Número / fecha", "Tipo", "Descripción", "Total", "Pendiente", "Estado"]}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Número / fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Tercero</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Pendiente</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map((movement) => (
                    <TableRow key={entityId(movement)} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>{movement.movementNumber}</Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatDateTime(movement.occurredAt)}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind} /></TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography noWrap sx={{ fontWeight: 750 }}>{movement.description || "—"}</Typography>
                        {movement.sourceNumber ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Ref. {movement.sourceNumber}</Typography> : null}
                      </TableCell>
                      <TableCell>{movement.party?.name || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>{formatMoney(movement.total, movement.currency)}</TableCell>
                      <TableCell align="right" sx={{ color: Number(movement.balanceDue) > 0 ? "warning.main" : ERP_BRAND.muted, fontWeight: 850 }}>
                        {formatMoney(movement.balanceDue, movement.currency)}
                      </TableCell>
                      <TableCell><MovementStatusChip status={movement.status} /></TableCell>
                      <TableCell align="right">
                        <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => setDetailId(entityId(movement))}><VisibilityRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        {movement.status === "draft" && (canUpdate || canConfirm) ? (
                          <>
                            {canUpdate ? <Tooltip title="Editar borrador"><IconButton size="small" onClick={() => { setEditing(movement); setFormOpen(true); }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}
                            {canConfirm ? <Tooltip title="Confirmar movimiento"><IconButton size="small" color="success" onClick={() => setAction({ type: "confirm", movement })}><CheckCircleRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}
                          </>
                        ) : null}
                        {movement.status === "confirmed" && canVoid ? (
                          <Tooltip title="Anular mediante reversa"><IconButton size="small" color="error" onClick={() => setAction({ type: "void", movement })}><DeleteForeverRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveOperationalTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage="Filas por página"
            />
          </>
        )}
      </ErpPanel>

      <MovementFormDialog
        open={formOpen}
        onClose={closeForm}
        onSaved={async () => {
          await Promise.all([load(), refreshBootstrap()]);
        }}
        projectId={projectId}
        movement={editing}
        bootstrap={bootstrap}
        access={access}
        initialKind={initialKind}
        initialProductId={initialProductId}
      />

      <MovementDetailDialog
        open={Boolean(detailId)}
        onClose={closeDetail}
        projectId={projectId}
        movementId={detailId}
        canReadCustomers={canReadCustomers && membershipsEnabled}
      />

      <Dialog open={Boolean(action)} onClose={processing ? undefined : () => setAction(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {action?.type === "confirm" ? "Confirmar movimiento" : "Anular movimiento"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {action?.type === "confirm" ? (
            <Alert severity="warning">
              Al confirmar se aplicarán stock, asientos contables, transacciones y caja. Luego no podrá editarse; cualquier corrección se realiza mediante una reversa.
            </Alert>
          ) : (
            <Stack spacing={2}>
              <Alert severity="warning">
                La anulación no borra el registro. Generará un movimiento inverso para conservar la trazabilidad.
              </Alert>
              <TextField
                fullWidth
                required
                multiline
                minRows={3}
                label="Motivo de la anulación"
                value={actionReason}
                onChange={(event) => setActionReason(event.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, "& .MuiButton-root": { minHeight: 44 } }}>
          <Button onClick={() => setAction(null)} disabled={processing}>Cancelar</Button>
          <Button variant="contained" color={action?.type === "confirm" ? "success" : "error"} onClick={performAction} disabled={processing}>
            {processing ? "Procesando..." : action?.type === "confirm" ? "Confirmar" : "Generar reversa"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
